import {
  Avatar,
  Box,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { useUser } from "../contexts/useUser";
import { useQuery } from "@tanstack/react-query";
import { fetchLLMCallsRecursive } from "../dataaccess/fetchLLMCalls";

type callPropsType = {
  calls?: any;
  showFilter: boolean;
  title?: string;
  avatarOnly?: boolean;
  limitDefault?: number;
  onClick?: any;
  selectedCall?: any;
};

const Calls: React.FC<callPropsType> = ({
  calls,
  showFilter,
  title,
  avatarOnly,
  onClick,
  selectedCall,
}: callPropsType) => {
  const { project } = useUser();

  const [limit, setLimit] = React.useState("10");
  const [status, setStatus] = React.useState("all");

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      "calls",
      { projectId: project?.id, status: status, limit: limit },
    ],
    queryFn: () =>
      fetchLLMCallsRecursive(project?.id ?? "", parseInt(limit), true, status),

    enabled: !calls && project?.id && project?.id.length > 0 ? true : false,
  });

  if (!calls && isLoading) {
    return <div>Loading...</div>;
  }

  if (!calls && isError) {
    return <div>Error...</div>;
  }

  const callsData = calls
    ? calls
    : data?.data?.callsByProjectIdAndCreatedAt?.items ?? [];

  return (
    <>
      <Box p={2}>
        {showFilter && (
          <Paper elevation={3} style={{borderRadius: 3, border: "1px solid #444", height: 100 }}>
            {title && <Typography variant="h6">{title}</Typography>}
            <FormControl sx={{ width: 250, margin: 2 }}>
              <InputLabel id="statusLabel">Filter status:</InputLabel>
              <Select
                labelId="statusLabel"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                }}
                label="Filter status:" // This prop is necessary for proper alignment and accessibility
                size="small"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="200">
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ bgcolor: "primary.main" }}>200</Avatar>
                    <span>&nbsp;&nbsp;200 - OK</span>
                  </div>
                </MenuItem>
                <MenuItem value="302">
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ bgcolor: "lightgreen" }}>302</Avatar>
                    &nbsp;&nbsp;302 - Cache Hit
                  </div>
                </MenuItem>
                <MenuItem value="500">
                  {" "}
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ bgcolor: "secondary.main" }}>5xx</Avatar>
                    &nbsp;&nbsp;5xx - Error
                  </div>
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ width: 200, margin: 2 }}>
              <InputLabel id="limitLabel">Show most recent:</InputLabel>
              <Select
                labelId="limitLabel"
                value={limit}
                onChange={(e) => {
                  setLimit(e.target.value);
                }}
                label="Show most recent:" // This prop is necessary for proper alignment and accessibility
                size="small"
              >
                <MenuItem value="10">10</MenuItem>
                <MenuItem value="25">25</MenuItem>
                <MenuItem value="50">50</MenuItem>
                <MenuItem value="100">100</MenuItem>
              </Select>
            </FormControl>
          </Paper>
        )}

        {avatarOnly && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
              width: "100%",
              height: "100%",
              flexWrap: "wrap",
            }}
          >
            {callsData.map((call: any) => (
              <Tooltip
                title={
                  call?.response?.length > 200
                    ? call.response.slice(0, 200) + "..."
                    : call.response
                }
                key={call?.id}
              >
                <Avatar
                  key={call?.id}
                  sx={{
                    bgcolor:
                      call?.status === "200"
                        ? "primary.main"
                        : call?.status === "302"
                        ? "lightgreen"
                        : "secondary.main",
                  }}
                >
                  {call?.status}
                </Avatar>
              </Tooltip>
            ))}
          </Box>
        )}

        {!avatarOnly && (
          <Box height="100%">
            <List dense>
              {callsData.map((call: any) => (
                <ListItem
                  key={call?.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: onClick ? "pointer" : "default",
                  }}
                  onClick={() => (onClick ? onClick(call) : null)}
                  selected={selectedCall?.id === call?.id}
                >
                  <ListItemAvatar>
                    <Tooltip
                      title={
                        call?.response?.length > 200
                          ? call.response.slice(0, 200) + "..."
                          : call.response
                      }
                    >
                      <Avatar
                        sx={{
                          bgcolor:
                            call?.status === "200"
                              ? "primary.main"
                              : call?.status === "302"
                              ? "lightgreen"
                              : "secondary.main",
                        }}
                      >
                        {call?.status}
                      </Avatar>
                    </Tooltip>
                  </ListItemAvatar>

                  <ListItemText
                    primary={`${call?.customer?.name ?? "System"} via ${
                      call?.service ? call?.service?.name : call?.model?.name ?? "cache"
                    } from ${call?.region}, ${call?.country}`}
                    secondary={` ${new Date(
                      call?.createdAt ?? ""
                    ).toLocaleString()}. Prompt: ${
                      call?.tokenCountPrompt
                    } tokens, Response: ${call?.tokenCountResponse} tokens`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
    </>
  );
};

export default Calls;
